import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Star,
  ArrowLeft,
  MessageSquare,
} from 'lucide-react';
import ReviewModal from '../../components/reviews/ReviewModal';
import { hasExistingReview } from '../../services/reviewsService';
import { useAppDispatch } from '../../store/hooks';
import { openOrCreateConversationByUserId } from '../../store/chatThunks';
import airportImage from '../../assets/airportt.png';

const PAGE_SIZE = 12;

const getStatusIcon = status => {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'accepted':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'rejected':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-gray-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300';
    case 'accepted':
      return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300';
    case 'rejected':
      return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300';
    case 'completed':
      return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300';
    case 'cancelled':
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:border-gray-800 dark:text-gray-300';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:border-gray-800 dark:text-gray-300';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'pending':
      return t('flexyRideApplications.statusPending');
    case 'accepted':
      return t('flexyRideApplications.statusAccepted');
    case 'rejected':
      return t('flexyRideApplications.statusRejected');
    case 'completed':
      return t('flexyRideApplications.statusCompleted');
    case 'cancelled':
      return t('flexyRideApplications.statusCancelled');
    default:
      return status?.toUpperCase() || t('flexyRideApplications.statusUnknown');
  }
};

export default function SeatListingRequests() {
  const navigate = useNavigate();
  const { listingId } = useParams();
  const { user, initializing } = usePublicAuth();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [listings, setListings] = useState({});
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({
    visible: false,
    reviewedUserId: null,
    reviewedUserName: '',
    requestId: null,
    role: 'passenger',
  });
  const [reviewStatuses, setReviewStatuses] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isMessaging, setIsMessaging] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    // Show applications for user's listings (listingOwnerId == currentUser.uid)
    // Only seat listings
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'ride_requests'),
        where('listingOwnerId', '==', user.uid),
      ),
      async snapshot => {
        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch listing details for each request
        const applicationsWithListings = await Promise.all(
          requests.map(async request => {
            try {
              const listingDoc = await getDoc(
                doc(db, 'ride_listings', request.listingId),
              );

              return {
                ...request,
                listing: listingDoc.exists()
                  ? { id: listingDoc.id, ...listingDoc.data() }
                  : null,
              };
            } catch (error) {
              console.error('Error fetching listing:', error);
              return { ...request, listing: null };
            }
          }),
        );

        // Filter by seat type and optional listingId
        const filteredApplications = applicationsWithListings.filter(app => {
          const type = app.listing?.type || app.listingType;
          const matchesType = type === 'seat';
          const matchesListing = listingId ? app.listingId === listingId : true;
          return matchesType && matchesListing;
        });

        filteredApplications.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
          return bTime - aTime;
        });

        setApplications(filteredApplications);

        // Store listings for easy access
        const listingsMap = {};
        filteredApplications.forEach(app => {
          if (app.listing) {
            listingsMap[app.listingId] = app.listing;
          }
        });
        setListings(listingsMap);
        setLoading(false);
      },
      error => {
        console.error('Error fetching applications:', error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user?.uid, listingId]);

  // Check review statuses
  useEffect(() => {
    const checkReviewStatuses = async () => {
      if (!user?.uid || applications.length === 0) return;

      const statuses = {};
      try {
        await Promise.all(
          applications.map(async application => {
            const reviewedUserId = application.applicantId;
            if (!reviewedUserId) {
              statuses[application.id] = false;
              return;
            }

            const hasReview = await hasExistingReview(
              reviewedUserId,
              application.id,
              user.uid,
            );
            statuses[application.id] = hasReview;
          }),
        );
        setReviewStatuses(statuses);
      } catch (error) {
        console.error('Error checking review statuses:', error);
      }
    };

    checkReviewStatuses();
  }, [applications, user?.uid]);

  const handleAccept = async applicationId => {
    try {
      if (!user?.uid) {
        alert(t('flexyRideSeatRequests.loginToAccept'));
        return;
      }

      const application = applications.find(app => app.id === applicationId);
      if (!application) {
        alert(t('flexyRideSeatRequests.applicationNotFound'));
        return;
      }

      // Security check: Verify that current user is the listing owner
      if (application.listingOwnerId !== user.uid) {
        alert(t('flexyRideSeatRequests.onlyOwnListings'));
        return;
      }

      const targetListingId = application.listingId;
      if (!targetListingId) {
        alert(t('flexyRideDetail.listingIdNotFound'));
        return;
      }

      // Verify listing ownership
      const listingDoc = await getDoc(doc(db, 'ride_listings', targetListingId));
      if (!listingDoc.exists()) {
        alert(t('flexyRideDetail.listingNotFound'));
        return;
      }

      const listingData = listingDoc.data();
      if (listingData.createdBy !== user.uid) {
        alert(t('flexyRideSeatRequests.onlyOwnListings'));
        return;
      }

      // Accept the selected application
      await updateDoc(doc(db, 'ride_requests', applicationId), {
        status: 'accepted',
        updatedAt: serverTimestamp(),
      });

      // Reject all other pending requests for the same listing
      const otherPendingRequests = await Promise.all(
        applications
          .filter(
            app =>
              app.listingId === targetListingId &&
              app.status === 'pending' &&
              app.id !== applicationId,
          )
          .map(app => app.id),
      );

      if (otherPendingRequests.length > 0) {
        const batch = writeBatch(db);
        otherPendingRequests.forEach(requestId => {
          batch.update(doc(db, 'ride_requests', requestId), {
            status: 'rejected',
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();
      }

      alert(t('flexyRideSeatRequests.acceptSuccess'));
    } catch (error) {
      console.error('Error accepting application:', error);
      alert(t('flexyRideSeatRequests.acceptError'));
    }
  };

  const handleReject = async applicationId => {
    try {
      if (!user?.uid) {
        alert(t('flexyRideSeatRequests.loginToReject'));
        return;
      }

      const application = applications.find(app => app.id === applicationId);
      if (!application) {
        alert(t('flexyRideSeatRequests.applicationNotFound'));
        return;
      }

      // Security check: Verify that current user is the listing owner
      if (application.listingOwnerId !== user.uid) {
        alert(t('flexyRideSeatRequests.onlyOwnListings'));
        return;
      }

      await updateDoc(doc(db, 'ride_requests', applicationId), {
        status: 'rejected',
        updatedAt: serverTimestamp(),
      });

      alert(t('flexyRideSeatRequests.rejectSuccess'));
    } catch (error) {
      console.error('Error rejecting application:', error);
      alert(t('flexyRideSeatRequests.rejectError'));
    }
  };

  const handleMessage = async application => {
    if (isMessaging) return;
    setIsMessaging(true);
    try {
      const applicantId = application.applicantId;
      if (!applicantId) {
        setIsMessaging(false);
        return;
      }

      const applicantDoc = await getDoc(doc(db, 'users', applicantId));
      if (!applicantDoc.exists()) {
        setIsMessaging(false);
        return;
      }

      const applicantData = applicantDoc.data();
      const applicantName =
        applicantData.displayName ||
        `${applicantData.firstName || ''} ${applicantData.lastName || ''}`.trim() ||
        applicantData.email?.split('@')[0] ||
        t('common.unknownUser');

      const conversation = await dispatch(
        openOrCreateConversationByUserId(applicantId, applicantName),
      );
      if (conversation?.id) {
        navigate(`/chat/${conversation.id}`);
      }
    } catch (e) {
      console.log('Failed to open message:', e?.message);
    } finally {
      setIsMessaging(false);
    }
  };

  const paginatedApplications = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return applications.slice(start, end);
  }, [applications, currentPage]);

  const totalPages = Math.ceil(applications.length / PAGE_SIZE);

  // Derive primary listing info for header card (like TravelerTripRequests)
  const primaryListing = useMemo(() => {
    if (!applications.length) return null;
    const first = applications[0];
    return first.listing || listings[first.listingId] || null;
  }, [applications, listings]);

  if (initializing) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        {/* Simple loading area inside hero-style layout */}
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[400px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-5/6 animate-pulse"></div>
              </div>
              <div className="relative">
                <div className="w-full h-80 bg-white/20 dark:bg-slate-700/50 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('flexyRideSeatRequests.loginToView')}
            </p>
            <a
              href="/auth/login"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('common.signIn')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      {/* Hero Section (matching TravelerTripRequests style) */}
      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[400px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('flexyRideSeatRequests.title')}
              </h1>
              <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                {t('flexyRideSeatRequests.subtitle')}
              </p>
            </div>
            <div className="relative">
              <img
                src={airportImage}
                alt="Airport"
                className="w-full h-auto object-contain"
                style={{ maxHeight: '350px' }}
              />
            </div>
          </div>
        </div>

        {/* Decorative bottom gradient */}
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-6 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-semibold">{t('common.back')}</span>
        </button>

        {/* Listing Info Card (top summary, similar to TravelerTripRequests) */}
        {primaryListing && (
          <div className="rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-slate-900 dark:to-slate-800 p-6 shadow-lg border border-gray-200 dark:border-slate-800 mb-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                  {t('flexyRideSeatRequests.route')}
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                  <span className="block">
                    {primaryListing.from || t('home.fromFallback')}
                  </span>
                  <span className="text-gray-400 dark:text-slate-500 mx-2 my-1 block">
                    ↓
                  </span>
                  <span className="block">
                    {primaryListing.to || t('home.toFallback')}
                  </span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 mb-1">
                    {t('travelerTripRequests.departure')}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {primaryListing.date || t('flexyRideSeat.dateNotSet')}{' '}
                    {primaryListing.time
                      ? t('tripDetail.atTime', { time: primaryListing.time })
                      : ''}
                  </p>
                </div>
              </div>

              {primaryListing.seats && (
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 mb-1">
                      {t('flexyRideSeatRequests.availableSeats')}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {primaryListing.price && primaryListing.currency
                        ? t('flexyRideSeatRequests.availableSeatsWithPrice', {
                            seats: primaryListing.seats,
                            currency: primaryListing.currency || 'CAD',
                            price: primaryListing.price,
                          })
                        : t('flexyRideSeatRequests.availableSeatsValue', {
                            seats: primaryListing.seats,
                          })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-gray-500 dark:text-slate-400">
              {t('common.loading')}
            </div>
          </div>
        ) : paginatedApplications.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-slate-900 p-12 shadow-lg border-2 border-gray-200 dark:border-slate-800 text-center">
            <MessageCircle className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-slate-400">
              {t('flexyRideSeatRequests.empty')}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedApplications.map(application => {
                const listing =
                  application.listing || listings[application.listingId];
                if (!listing) return null;

                const isPending = application.status === 'pending';
                const isCompleted = application.status === 'completed';
                const isAccepted = application.status === 'accepted';

                return (
                  <div
                    key={application.id}
                    className="group relative rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800 hover:shadow-xl hover:border-gray-300 dark:hover:border-slate-700 transition-all duration-300"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-black dark:bg-slate-700 flex items-center justify-center">
                          <User className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 dark:text-white truncate">
                            {application.applicantName ||
                              t('common.unknownUser')}
                          </h3>
                          <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
                            {t('flexyRideSeatRequests.requestingPassenger')}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusColor(
                          application.status,
                        )} flex-shrink-0`}
                      >
                        {getStatusIcon(application.status)}
                        <span className="text-xs font-bold">
                          {getStatusLabel(application.status, t)}
                        </span>
                      </div>
                    </div>

                    {/* Route */}
                    <div className="flex items-start gap-2 mb-4">
                      <MapPin className="h-4 w-4 text-gray-500 dark:text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                          {listing.from || t('home.fromFallback')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          →
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1">
                          {listing.to || t('home.toFallback')}
                        </p>
                      </div>
                    </div>

                    {/* Date & Time */}
                    <div className="flex items-center gap-2 mb-3 text-sm text-gray-600 dark:text-slate-400">
                      <CalendarDays className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                      <span>
                        {listing.date || t('flexyRideSeat.dateNotSet')} •{' '}
                        {listing.time || t('flexyRideSeat.timeNotSet')}
                      </span>
                    </div>

                    {/* Price block */}
                    {listing.price && (
                      <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-300">
                            {t('flexyRideSeatRequests.price')}
                          </span>
                          <span className="text-sm font-bold text-yellow-900 dark:text-yellow-200">
                            {t('flexyRideMySeatListings.pricingValue', {
                              currency: listing.currency || 'CAD',
                              price: listing.price,
                              seats: listing.seats || 1,
                            })}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Completed Status */}
                    {isCompleted && (
                      <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <p className="text-sm font-semibold text-green-900 dark:text-green-200 mb-2">
                          {t('flexyRideSeatRequests.paymentCompleted')}
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300">
                          {t('flexyRideSeatRequests.paymentCompletedMessage')}
                        </p>
                        {reviewStatuses[application.id] ? (
                          <div className="mt-3 px-3 py-1 bg-gray-100 dark:bg-slate-700 rounded-lg inline-block">
                            <span className="text-xs font-semibold text-gray-600 dark:text-slate-400">
                              {t('flexyRideApplications.reviewed')}
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              if (!reviewStatuses[application.id]) {
                                const reviewedUserId = application.applicantId;
                                const reviewedUserName =
                                  application.applicantName ||
                                  t('common.unknownUser');
                                setReviewModal({
                                  visible: true,
                                  reviewedUserId,
                                  reviewedUserName,
                                  requestId: application.id,
                                  role: 'passenger',
                                });
                              }
                            }}
                            disabled={reviewStatuses[application.id]}
                            className="mt-3 flex items-center gap-2 px-3 py-1.5 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Star className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                            <span className="text-xs font-semibold text-yellow-800 dark:text-yellow-300">
                              {t('flexyRideApplications.rate')}
                            </span>
                          </button>
                        )}
                      </div>
                    )}

                    {/* Accepted Status */}
                    {isAccepted && (
                      <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                          {t('flexyRideSeatRequests.rideInProgress', {
                            name:
                              application.applicantName ||
                              t('common.unknownUser'),
                          })}
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {isPending && (
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => handleReject(application.id)}
                          className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition"
                        >
                          {t('common.reject')}
                        </button>
                        <button
                          onClick={() => handleAccept(application.id)}
                          className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition"
                        >
                          {t('common.accept')}
                        </button>
                      </div>
                    )}

                    {/* Message and Support Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleMessage(application)}
                        disabled={isMessaging}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition disabled:opacity-50"
                      >
                        <MessageCircle className="h-4 w-4" />
                        {isMessaging
                          ? t('chat.opening')
                          : t('myRequests.message')}
                      </button>
                      <button
                        onClick={() => navigate('/profile/support')}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition"
                      >
                        <MessageSquare className="h-4 w-4" />
                        {t('profile.supportTitle') || 'Support'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.prev')}
                </button>
                <span className="text-sm text-gray-600 dark:text-slate-400">
                  {t('common.pageOf', {
                    current: currentPage,
                    total: totalPages,
                  })}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <ReviewModal
        visible={reviewModal.visible}
        onClose={() =>
          setReviewModal({
            visible: false,
            reviewedUserId: null,
            reviewedUserName: '',
            requestId: null,
            role: 'passenger',
          })
        }
        reviewedUserId={reviewModal.reviewedUserId}
        reviewedUserName={reviewModal.reviewedUserName}
        requestId={reviewModal.requestId}
        userRole={reviewModal.role}
        onReviewSubmitted={() => {
          if (reviewModal.requestId) {
            setReviewStatuses(prev => ({
              ...prev,
              [reviewModal.requestId]: true,
            }));
          }
        }}
      />
    </div>
  );
}

